import {
  ChatContainerTypes,
  ChatCustomBlockTypes,
  FsmDefMetaTypes,
  type BlockButtons,
  type BlockSteps,
  type ChatMessage,
} from "@do/shared-types";
import { cn } from "@do/ui/lib/utils";
import { AnimatePresence, motion, useIsPresent } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMotionEnabled } from "../../../hooks/use-motion-enabled";
import { aggregateBlocks } from "../../../lib/aggregate-blocks";
import { getMinButtonDelayMs } from "../../../lib/button-delays";
import {
  combineBlocksAndContainers,
  getBlockIdentifier,
  isCombinedBlock,
  isCombinedContainer,
} from "../../../lib/combine-blocks-and-containers";
import { isEmptyTextBlock } from "../../../lib/is-empty-text-block";
import { markdownToPlainText } from "../../../lib/markdown-to-text";
import { isErrorMessage as isErrorMessageFunc } from "../../../lib/merge-bot-messages";
import {
  getRevealProps,
  INLINE_BUTTONS_COLLAPSE_TRANSITION,
  MESSAGE_REVEAL_OFFSET_Y,
  MESSAGE_REVEAL_TIMING,
} from "../../../lib/motion-presets";
import { useChat } from "../../../providers/chat-provider";
import { FooterButtons } from "../../footer/footer-buttons";
import { MessageActions } from "./actions";
import { AdvisorAvatar } from "./advisor-avatar";
import { MessageAgentProcess } from "./agent-process";
import { MessageButtons } from "./buttons";
import { MessageCard } from "./containers/card";
import { MessageCarousel } from "./containers/carousel";
import { MessageGridCards } from "./containers/grid";
import { Loader } from "./loader";
import { MessageMedia } from "./media";
import { MessageSeparator } from "./separator";
import { MessageSteps } from "./steps";
import { MessageStepFormSuccess } from "./steps/form-success";
import { MessageText } from "./text";

const RENDERABLE_BLOCK_TYPES = new Set<ChatCustomBlockTypes | ChatContainerTypes>([
  ChatCustomBlockTypes.agentProcess,
  ChatCustomBlockTypes.bouncingDots,
  ChatCustomBlockTypes.separator,
  ChatCustomBlockTypes.text,
  ChatCustomBlockTypes.formSuccess,
  ChatCustomBlockTypes.steps,
  ChatCustomBlockTypes.media,
  ChatContainerTypes.card,
]);

/**
 * Holds a message's inline buttons, and once they have faded out closes the room they held,
 * so what sits below glides up instead of jumping when their node goes.
 *
 * The flex gap above them would still drop in one go, so it is traded for padding inside the
 * collapsing box. The box does not clip: the buttons are invisible by the time it shrinks past
 * them, while clipping from the start would cut them off as they sink during their fade.
 * Invisible is not gone, though, so they stop taking clicks as soon as they start leaving.
 */
const InlineButtons = ({
  motionEnabled,
  children,
}: {
  motionEnabled: boolean;
  children: React.ReactNode;
}) => {
  const isPresent = useIsPresent();

  return (
    <motion.div
      className={cn("-mt-(--message-gap)", !isPresent && "pointer-events-none")}
      exit={
        motionEnabled ? { height: 0, transition: INLINE_BUTTONS_COLLAPSE_TRANSITION } : undefined
      }
    >
      <div className="pt-(--message-gap)">{children}</div>
    </motion.div>
  );
};

type MessageProps = {
  message: ChatMessage;
  index: number;
  firstMessageRef: React.RefObject<HTMLDivElement | null>;
  handleImageLoad: () => void;
  /**
   * Mount without the entrance animation, for content the viewer is already looking at (the
   * message that replaced the streaming placeholder). It still leaves like any other, and its
   * buttons still cascade in: they are new.
   */
  skipReveal?: boolean;
  /**
   * The list is showing this message again, not for the first time: no entrance for it nor
   * for its buttons.
   */
  isRestored?: boolean;
  /**
   * Told whether this message's own buttons are showing, so the list can wait out their exit
   * when `showOnlyLast` moves them to a newer answer.
   */
  onButtonsShownChange?: (shown: boolean) => void;
  /**
   * Hold the entrance back this long. Set when this message arrives in the same commit that
   * removed another one, so the two do not play over each other.
   */
  revealDelayS?: number;
  /**
   * `AnimatePresence` clones this component with a ref in `popLayout` mode, to measure
   * the message before pinning it out of the flow while it leaves.
   */
  ref?: React.Ref<HTMLDivElement>;
};

export const Message = ({
  firstMessageRef,
  message,
  index: messageIndex,
  handleImageLoad,
  skipReveal = false,
  isRestored = false,
  onButtonsShownChange,
  revealDelayS = 0,
  ref,
}: MessageProps) => {
  const {
    isTestingMode,
    isLiveChatActive,
    isEditingMode,
    onEditBotResponse,
    lastMessageRef,
    lastMessageId,
    chatbotParams,
    messages,
    noActions,
    lastUserMessageId,
    lastUserMessageRef,
    hasPreviousStep,
    canBackToPreviousStep: hasBackTarget,
    onAgentProcessStepHover,
  } = useChat();
  const t = useTranslations("do-chat-v2");
  const motionEnabled = useMotionEnabled();

  const isStreaming = useMemo(() => message.id === "streamed-msg", [message.id]);
  // Frozen at mount: the wait belongs to the arrival this message made, and the list stops
  // reporting a departure as soon as it is over. Re-reading it would hand a message that is
  // already mid-flight a transition it never asked for.
  const mountRevealDelaySRef = useRef(revealDelayS);
  // Frozen too: whether this node arrived at rest is decided by how it mounted.
  const mountSkipEntranceRef = useRef(skipReveal || isRestored);
  const revealProps = getRevealProps({
    motionEnabled,
    // Skipping the entrance never takes the exit away: the message still leaves like any other.
    skipEntrance: mountSkipEntranceRef.current,
    timing: MESSAGE_REVEAL_TIMING,
    // Same rise in every scroll mode: the top-anchored ones measure the message without
    // its transform (`getLayoutTop`), so it is pinned where it settles.
    offsetY: MESSAGE_REVEAL_OFFSET_Y,
    delayS: mountRevealDelaySRef.current,
    // The streaming placeholder is swapped for the definitive message rather than
    // removed: letting it leave would play its departure over its own replacement.
    withExit: motionEnabled && !isStreaming,
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const isLastUserMessage = lastUserMessageId === message.id;

  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  // The scroll logic pins the last user message by this node. Publishing it from an
  // effect rather than a second ref keeps a single owner: effects of children run
  // before the `Messages` effect that reads it, and the cleanup only clears the entry
  // this message put there, so handing over to another message cannot blank it.
  useEffect(() => {
    if (!isLastUserMessage || !lastUserMessageRef) {
      return;
    }

    const node = rootRef.current;
    lastUserMessageRef.current = node;

    return () => {
      if (lastUserMessageRef.current === node) {
        lastUserMessageRef.current = null;
      }
    };
  }, [isLastUserMessage, lastUserMessageRef]);

  const prevMessageIsUser = Boolean(messages[messageIndex - 1]?.user);
  const nextMessageIsUser = Boolean(messages[messageIndex + 1]?.user);
  const isConsecutiveUserMessage = Boolean(message.user && prevMessageIsUser);
  const isFollowedByUserMessage = Boolean(message.user && nextMessageIsUser);

  const renderButtonsInFooter =
    chatbotParams.buttons?.position === "bottom" && Boolean(chatbotParams.buttons?.showOnlyLast);

  const metadataStr = JSON.stringify(Object.assign({}, ...(message.metadata || [])));

  const combinedBlocksAndContainers = combineBlocksAndContainers({
    blocks: message.blocks ?? [],
    containers: message.containers ?? [],
  });

  const aggregatedBlocks = aggregateBlocks(combinedBlocksAndContainers ?? []);

  const gridType = useMemo(() => {
    return aggregatedBlocks.grid?.blocks?.[0]?.type as
      | ChatContainerTypes.card
      | ChatCustomBlockTypes.buttons
      | undefined;
  }, [aggregatedBlocks.grid?.blocks]);

  const visibleRenderableBlocks = useMemo(() => {
    return aggregatedBlocks.blocks
      .map((block, index) => ({ block, index }))
      .filter(
        ({ block }) => block.type !== ChatCustomBlockTypes.liveChat && !isEmptyTextBlock(block)
      )
      .filter(({ block }) =>
        RENDERABLE_BLOCK_TYPES.has(block.type as ChatCustomBlockTypes | ChatContainerTypes)
      );
  }, [aggregatedBlocks.blocks]);

  const combinedButtonsBlock: BlockButtons | null = useMemo(() => {
    if (aggregatedBlocks.buttons.length === 0) return null;
    return {
      type: ChatCustomBlockTypes.buttons,
      buttons: aggregatedBlocks.buttons,
    };
  }, [aggregatedBlocks.buttons]);

  const isLastNonErrorBotMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const candidate = messages[i];
      if (!candidate?.user && !isErrorMessageFunc(candidate)) return messageIndex === i;
    }
    return false;
  }, [messageIndex, messages]);

  // Consecutive bot messages are merged, which concatenates their metadata lists.
  const isGenerative = useMemo(
    () =>
      Array.isArray(message.metadata) &&
      message.metadata.some((meta) => Boolean(meta?.["generative_response"])),
    [message]
  );

  const isResponseEditionEnabled = useMemo(() => {
    return !!(isTestingMode && isEditingMode && isGenerative && onEditBotResponse);
  }, [isTestingMode, isEditingMode, onEditBotResponse, isGenerative]);

  const hasActions = useMemo(() => {
    return (
      aggregatedBlocks.blocks.length > 0 &&
      aggregatedBlocks.blocks.some((block) => block.type === ChatCustomBlockTypes.text) &&
      !aggregatedBlocks.blocks.some((block) => block.type === ChatCustomBlockTypes.separator) &&
      (chatbotParams.enableCopyMessageButton ||
        chatbotParams.ttsEnabled ||
        isGenerative ||
        (isResponseEditionEnabled && onEditBotResponse))
    );
  }, [
    aggregatedBlocks.blocks,
    chatbotParams.enableCopyMessageButton,
    chatbotParams.ttsEnabled,
    isGenerative,
    isResponseEditionEnabled,
    onEditBotResponse,
  ]);

  const isErrorMessage = useMemo(() => isErrorMessageFunc(message), [message]);

  const hasOnlyOneStepsForm = useMemo(() => {
    const visibleBlocks = aggregatedBlocks.blocks
      .filter(isCombinedBlock)
      .filter((block) => block.type !== ChatCustomBlockTypes.liveChat && !isEmptyTextBlock(block));

    return visibleBlocks.length === 1 && visibleBlocks[0].type === ChatCustomBlockTypes.steps;
  }, [aggregatedBlocks.blocks]);

  const hasStepsForm = useMemo(() => {
    const stepsBlock = visibleRenderableBlocks
      .filter(({ block }) => isCombinedBlock(block))
      .find(({ block }) => block.type === ChatCustomBlockTypes.steps)?.block as
      | BlockSteps
      | undefined;
    if (!stepsBlock) return false;

    const initialMeta = stepsBlock.fsmDef?.states?.[stepsBlock.fsmDef.initial]?.meta;
    return initialMeta?.type === FsmDefMetaTypes.form;
  }, [visibleRenderableBlocks]);

  const shouldShowButtons = useMemo(() => {
    if (
      (!combinedButtonsBlock &&
        !aggregatedBlocks.grid &&
        gridType !== ChatCustomBlockTypes.buttons) ||
      message.user ||
      isLiveChatActive ||
      isErrorMessage ||
      hasStepsForm
    )
      return false;
    if (chatbotParams.buttons?.showOnlyLast) return isLastNonErrorBotMessage;
    return true;
  }, [
    chatbotParams.buttons?.showOnlyLast,
    combinedButtonsBlock,
    aggregatedBlocks.grid,
    gridType,
    hasStepsForm,
    isErrorMessage,
    isLastNonErrorBotMessage,
    isLiveChatActive,
    message.user,
  ]);

  const canBackToPreviousStep = useMemo(() => {
    if (!chatbotParams.buttons?.showOnlyLast) return false;
    // Also off while a livechat runs, and for anything before its end.
    if (!hasBackTarget) return false;
    // When the host manages back navigation (e.g. `onlyLastMessage` mode), the
    // conversation history lives outside the `messages` array, so rely on the
    // host-provided flag instead of counting bot messages.
    if (hasPreviousStep !== undefined) return isLastNonErrorBotMessage;
    if (messages.filter((message) => !message.user).length <= 1) return false;
    return isLastNonErrorBotMessage;
  }, [
    chatbotParams.buttons?.showOnlyLast,
    hasBackTarget,
    hasPreviousStep,
    isLastNonErrorBotMessage,
    messages,
  ]);

  const isGridButtons = gridType === ChatCustomBlockTypes.buttons;

  const buttonsBlock: BlockButtons = useMemo(
    () =>
      isGridButtons
        ? (aggregatedBlocks.grid?.blocks?.[0] as BlockButtons)
        : combinedButtonsBlock || { type: ChatCustomBlockTypes.buttons, buttons: [] },
    [isGridButtons, aggregatedBlocks.grid?.blocks, combinedButtonsBlock]
  );

  const showBackButtonInButtons =
    canBackToPreviousStep &&
    chatbotParams.buttons?.backButtonPosition === "buttons" &&
    !hasStepsForm;

  // On its own the back button is enough to show the actions row, otherwise it never appears
  // when copy, TTS and edition are all off.
  const showBackButtonInActions =
    canBackToPreviousStep && chatbotParams.buttons?.backButtonPosition === "actions";

  // Include delays so the footer re-syncs when only a delay changes.
  const footerButtonsContentKey = useMemo(
    () =>
      [
        message.id ?? messageIndex,
        isGridButtons ? "grid" : "flat",
        showBackButtonInButtons ? "back" : "no-back",
        `block-delay:${buttonsBlock.delay ?? 0}`,
        ...(buttonsBlock.buttons ?? []).map(
          (button, index) =>
            `${button.cuid ?? button.id ?? button.title ?? `button-${index}`}:delay${
              button.delay ?? 0
            }`
        ),
      ].join("|"),
    [message.id, messageIndex, isGridButtons, showBackButtonInButtons, buttonsBlock]
  );

  // Back button is immediate; otherwise wait for the earliest button delay before
  // registering an empty footer slot.
  const footerMinDelayMs = useMemo(() => {
    if (showBackButtonInButtons) return 0;
    return getMinButtonDelayMs(buttonsBlock.buttons ?? [], buttonsBlock.delay ?? 0);
  }, [buttonsBlock.buttons, buttonsBlock.delay, showBackButtonInButtons]);

  const [footerButtonsReady, setFooterButtonsReady] = useState(footerMinDelayMs <= 0);

  useEffect(() => {
    if (footerMinDelayMs <= 0) {
      setFooterButtonsReady(true);
      return;
    }

    setFooterButtonsReady(false);
    const timer = window.setTimeout(() => setFooterButtonsReady(true), footerMinDelayMs);
    return () => window.clearTimeout(timer);
  }, [footerMinDelayMs]);

  const hasButtons = shouldShowButtons || showBackButtonInButtons;

  // Reported after every commit, before the next change is rendered, so the list reads what
  // was actually on screen when it decides whether an arrival has buttons to wait out.
  useLayoutEffect(() => {
    onButtonsShownChange?.(hasButtons);
  });

  return (
    <motion.div
      {...revealProps}
      id={message.id}
      className={cn(
        "flex flex-row gap-(--message-advisor-gap) w-full",
        (isConsecutiveUserMessage || hasOnlyOneStepsForm) && "-mt-(--message-gap)"
      )}
      role="group"
      aria-roledescription={t("chat.messages.accessibility.message-roledescription")}
      aria-label={
        message.advisor
          ? t("chat.messages.accessibility.message-advisor-aria-label")
          : message.user
          ? t("chat.messages.accessibility.message-user-aria-label")
          : t("chat.messages.accessibility.message-bot-aria-label")
      }
      ref={setRootRef}
    >
      {message.advisor && <AdvisorAvatar />}

      <div className={cn("flex flex-col gap-(--message-gap) w-full")}>
        {visibleRenderableBlocks.map(({ block, index }, visibleIndex) => {
          const blockIdentifier = getBlockIdentifier(block, index);
          const shouldAttachLastMessageRef =
            lastMessageId === message.id && visibleIndex === visibleRenderableBlocks.length - 1;
          const isStepsForm =
            hasStepsForm &&
            !hasOnlyOneStepsForm &&
            block.type === ChatCustomBlockTypes.steps &&
            "fsmDef" in block &&
            block.fsmDef?.states?.[block.fsmDef.initial]?.meta?.type === FsmDefMetaTypes.form;

          if (isCombinedBlock(block)) {
            if (block.type === ChatCustomBlockTypes.liveChat || isEmptyTextBlock(block)) {
              return null;
            }
          }

          return (
            <div
              key={blockIdentifier}
              id={`message-${message.id}-block-${blockIdentifier}-container`}
              ref={
                shouldAttachLastMessageRef
                  ? lastMessageRef
                  : visibleIndex === 0
                  ? firstMessageRef
                  : undefined
              }
              className={cn(isStepsForm && "hidden")}
              data-metadata={metadataStr}
            >
              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.bouncingDots && (
                <Loader allowThinking />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.agentProcess && (
                <MessageAgentProcess
                  block={block}
                  id={`message-${message.id}-block-${blockIdentifier}-agent-process`}
                  onStepHover={onAgentProcessStepHover}
                />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.separator && (
                <MessageSeparator
                  text={block.text}
                  id={`message-${message.id}-block-${blockIdentifier}-separator`}
                />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.text && (
                <MessageText
                  block={block}
                  metadata={block.metadata}
                  messageId={message.id}
                  messageIndex={messageIndex}
                  id={`message-${message.id}-block-${blockIdentifier}-text`}
                  idx={index}
                  isUser={message.user ?? false}
                  handleImageLoad={handleImageLoad}
                  isErrorMessage={isErrorMessage}
                  isConsecutiveUserMessage={isConsecutiveUserMessage}
                  isFollowedByUserMessage={isFollowedByUserMessage}
                  isConsecutiveBotTextBlock={
                    !message.user &&
                    visibleRenderableBlocks[visibleIndex - 1]?.block?.type ===
                      ChatCustomBlockTypes.text
                  }
                  isFollowedByBotTextBlock={
                    !message.user &&
                    visibleRenderableBlocks[visibleIndex + 1]?.block?.type ===
                      ChatCustomBlockTypes.text
                  }
                />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.media && (
                <MessageMedia
                  id={`message-${message.id}-block-${blockIdentifier}-media`}
                  media={block}
                />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.steps && (
                <MessageSteps block={block} messageIndex={messageIndex} messageId={message.id} />
              )}

              {isCombinedBlock(block) && block.type === ChatCustomBlockTypes.formSuccess && (
                <MessageStepFormSuccess
                  className={block.html_classes ?? undefined}
                  id={block.html_id ?? block.id}
                  content={block}
                />
              )}

              {isCombinedContainer(block) && block.type === ChatContainerTypes.card && (
                <MessageCard
                  id={`message-${message.id}-block-${blockIdentifier}-card`}
                  block={block}
                />
              )}
            </div>
          );
        })}

        {!message.user &&
          !isErrorMessage &&
          (hasActions || showBackButtonInActions) &&
          !isStreaming &&
          !noActions && (
            <MessageActions
              id={message.id ?? `message-${messageIndex}`}
              text={markdownToPlainText(
                aggregatedBlocks.blocks
                  .filter(isCombinedBlock)
                  .filter((block) => block.type === ChatCustomBlockTypes.text)
                  .map((block) => block.text)
                  .join("\n\n")
              )}
              isResponseEditionEnabled={isResponseEditionEnabled}
              canBackToPreviousStep={showBackButtonInActions}
              usedSourcesMetadatas={message.used_sources_metadatas ?? []}
              isGenerative={isGenerative}
              revealDelayS={mountRevealDelaySRef.current}
            />
          )}

        {aggregatedBlocks.grid && gridType === ChatContainerTypes.card && (
          <div data-metadata={metadataStr}>
            <MessageGridCards
              grid={aggregatedBlocks.grid}
              messageId={message.id}
              revealDelayS={mountRevealDelaySRef.current}
            />
          </div>
        )}

        {aggregatedBlocks.carousel && (
          <div data-metadata={metadataStr}>
            <MessageCarousel carousel={aggregatedBlocks.carousel} messageId={message.id} />
          </div>
        )}

        {(() => {
          const buttonsNode = (
            <div
              // In footer mode this node is handed to `setButtonsContent` and rendered
              // in a fixed slot of the footer. Without a key React reconciles the
              // incoming buttons onto the outgoing ones — same element type, same
              // position — so nothing remounts and the entrance animation never plays
              // after the very first set. Keying on the buttons' identity makes each
              // new set a new subtree. The key also changes when a delayed button set
              // is replaced, not when one of its buttons reaches its own delay.
              key={renderButtonsInFooter ? footerButtonsContentKey : undefined}
              data-metadata={metadataStr}
            >
              <MessageButtons
                block={buttonsBlock}
                canBackToPreviousStep={showBackButtonInButtons}
                isGrid={isGridButtons}
                initialElapsedMs={renderButtonsInFooter ? footerMinDelayMs : 0}
                // In the footer the buttons arrive on their own, not with this message.
                revealDelayS={renderButtonsInFooter ? 0 : mountRevealDelaySRef.current}
              />
            </div>
          );

          if (renderButtonsInFooter) {
            // The footer owns the presence here: this component is gone by the time the
            // buttons it registered are replaced, so it cannot hold them on screen.
            return hasButtons && footerButtonsReady ? (
              <FooterButtons contentKey={footerButtonsContentKey}>{buttonsNode}</FooterButtons>
            ) : null;
          }

          // This message outlives its own buttons — `showOnlyLast` drops them as soon as
          // a newer bot message lands — so it can hold them until they have animated out.
          // A fixed key keeps that to real appearances and departures, rather than
          // replaying whenever the back button joins or leaves the set.
          // Buttons arriving with their message cascade in, exactly as they do in the footer;
          // only a restored conversation keeps them still.
          return (
            <AnimatePresence initial={!isRestored}>
              {hasButtons && (
                <InlineButtons key="message-buttons" motionEnabled={motionEnabled}>
                  {buttonsNode}
                </InlineButtons>
              )}
            </AnimatePresence>
          );
        })()}
      </div>
    </motion.div>
  );
};
